import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/utils";

type SheetSide = "right" | "left" | "bottom";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  side?: SheetSide;
}

interface SheetContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: SheetSide;
  children: React.ReactNode;
  showCloseButton?: boolean;
}

const slideClasses: Record<SheetSide, string> = {
  right: cn(
    "right-0 top-0 h-full w-full max-w-[500px]",
    "data-[state=open]:animate-[sheetSlideInRight_200ms_var(--ease-standard)]",
    "data-[state=closed]:animate-[sheetSlideOutRight_200ms_var(--ease-standard)]"
  ),
  left: cn(
    "left-0 top-0 h-full w-full max-w-[500px]",
    "data-[state=open]:animate-[sheetSlideInLeft_200ms_var(--ease-standard)]",
    "data-[state=closed]:animate-[sheetSlideOutLeft_200ms_var(--ease-standard)]"
  ),
  bottom: cn(
    "bottom-0 left-0 w-full",
    "data-[state=open]:animate-[sheetSlideInBottom_200ms_var(--ease-standard)]",
    "data-[state=closed]:animate-[sheetSlideOutBottom_200ms_var(--ease-standard)]"
  ),
};

/** Sheet — slide-in panel built on Radix Dialog with portal rendering. */
export function Sheet({ open, onOpenChange, children }: SheetProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;

export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(
  ({ side = "right", children, showCloseButton = true, className, ...props }, ref) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-[var(--z-overlay)] bg-black/40 backdrop-blur-sm [will-change:opacity]",
          "data-[state=open]:animate-[sheetOverlayIn_200ms_ease-out]",
          "data-[state=closed]:animate-[sheetOverlayOut_200ms_ease-out]"
        )}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-[var(--z-modal)] flex transform-gpu flex-col will-change-transform",
          "bg-[var(--card)] shadow-[var(--shadow-xl)]",
          slideClasses[side],
          className
        )}
        aria-describedby={undefined}
        {...props}
      >
        {showCloseButton && (
          <DialogPrimitive.Close
            className={cn(
              "absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md",
              "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
              "hover:bg-[var(--muted)] transition-colors",
              "focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus-ring)]"
            )}
            aria-label="Close"
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
);
SheetContent.displayName = "SheetContent";

export const SheetHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-6 py-4 border-b border-[var(--border)] shrink-0", className)}
      {...props}
    />
  )
);
SheetHeader.displayName = "SheetHeader";

export const SheetBody = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex-1 overflow-y-auto p-6", className)} {...props} />
  )
);
SheetBody.displayName = "SheetBody";

export const SheetFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-6 py-4 border-t border-[var(--border)] flex justify-end gap-3 shrink-0",
        className
      )}
      {...props}
    />
  )
);
SheetFooter.displayName = "SheetFooter";

export const SheetTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-[var(--foreground)]", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";
